import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../seo/SEO'
import InfoPage from './InfoPage'

const About: React.FC = () => (
  <>
    <SEO
      title="About MakeMyDay | Who We Are and Why We Built It"
      description="MakeMyDay is a small team building calm, positive technology: a daily message, a small action and a kind community. Meet the people behind it and what we believe."
    />
    <InfoPage
      subtitle="About Us"
      title="Technology That Leaves You Feeling Better"
      lead="MakeMyDay is a small, independent team building a calmer kind of app: one that gives you a good moment each day instead of asking for more of your attention."
    >
      <h2>Why we built MakeMyDay</h2>
      <p>
        Most of the apps on our phones are designed to keep us scrolling. They are loud, they are
        urgent, and they rarely leave us feeling better than before we opened them. We wanted to
        build the opposite: something you can open for a minute, that gives you a thought worth
        keeping and a small thing to do, and then lets you get on with your day.
      </p>
      <p>
        The idea is simple. Happiness is rarely one big event. It is built from small, repeated
        moments: a kind sentence at the right time, a short pause, a habit you keep. MakeMyDay
        exists to deliver those moments reliably, at the time of day you choose, without pressure
        and without noise.
      </p>

      <h2>What MakeMyDay does</h2>
      <p>
        Every day, MakeMyDay gives you two things: a short uplifting <strong>message</strong> and a
        simple <strong>action</strong>, such as writing down one thing you are grateful for, taking
        a slow walk or sending a kind word to a friend. Both arrive at the hour you pick, in your
        own time zone.
      </p>
      <p>
        Around that sits a <strong>community</strong> where people share short positive notes with
        each other. You can like a note, save it to read again, and follow the people whose words
        you enjoy. Every note is checked automatically as it is shared, and anything borderline
        is reviewed by our team, so the space stays kind.
        You can read the details on our <Link to="/how-it-works">How it works</Link> page.
      </p>

      <h2>What we believe</h2>
      <h3>Positivity with a purpose</h3>
      <p>
        We are not interested in forced optimism or empty slogans. A good message acknowledges
        that days can be hard and offers something genuinely useful: perspective, calm, a reason to
        smile, or a small step forward.
      </p>
      <h3>Calm over attention</h3>
      <p>
        We measure success by whether MakeMyDay makes your day a little better, not by how long you
        spend in it. The daily message and action take a minute or two, and once you have read
        them, MakeMyDay has nothing more it needs from you that day.
      </p>
      <h3>Kindness and safety</h3>
      <p>
        A positive space only stays positive if it is looked after. Community notes go through
        automated checks before they are published, people can report anything that feels wrong,
        our team reviews what is flagged, and you can block anyone you would rather not hear from.
      </p>
      <h3>Respect for your data</h3>
      <p>
        We collect only what we need to run the service and we never sell personal data. You can
        delete your account yourself at any time. Our <Link to="/privacy">Privacy Policy</Link>{' '}
        explains exactly what we store and why.
      </p>

      <h2>The team</h2>
      <p>
        MakeMyDay is built by its three co-founders. We started it because we believe technology
        can do more than optimise productivity: it can support well-being, kindness and human
        connection.
      </p>
      <ul>
        <li>
          <strong>Pece Eminagov</strong>, co-founder, product. Shapes what MakeMyDay is and how it
          feels to use.
        </li>
        <li>
          <strong>Aleksandar Kotevski</strong>, co-founder, engineering. Builds the apps and the
          systems behind them.
        </li>
        <li>
          <strong>Boban Lazarevski</strong>, co-founder, platform. Keeps the infrastructure
          running and the platform ready to grow.
        </li>
      </ul>
      <p>
        We use AI to help write the daily messages and actions, but people decide what MakeMyDay
        should sound like and review what the community flags.
      </p>

      <h2>Where we are today</h2>
      <p>
        MakeMyDay started as a <a href="https://t.me/i_make_your_day_bot">Telegram bot</a> that
        anyone can use today. Our Android and iOS apps, along with a web version, are being
        prepared for release, and we share what we learn along the way on our{' '}
        <a href="https://blog.makemyday.tips">blog</a>. Longer term, we plan to let creators build
        their own positive voices on MakeMyDay; you can follow those plans on our{' '}
        <a href="/home#roadmap">roadmap</a> and in our{' '}
        <a href="https://docs.makemyday.tips">documentation</a>.
      </p>

      <h2>Get in touch</h2>
      <p>
        Write to us at{' '}
        <a href="mailto:hello@makemyday.tips">hello@makemyday.tips</a> with ideas, feedback or
        questions, or reach us on any of the social channels linked at the bottom of this page.
        For privacy requests, use <a href="mailto:info@makemyday.tips">info@makemyday.tips</a>.
      </p>
    </InfoPage>
  </>
)

export default About
